import React, { useCallback, useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { ChevronDown, ChevronRight } from 'lucide-react';
import clsx from 'clsx';
import { Parameter, EventSchema } from '../../types/schema';
import { ParameterViewMode } from './ParameterListContainer'; // Import ParameterViewMode
import ConfirmDialog from '../dialogs/ConfirmDialog';
import ParameterTreeEditForm from './ParameterTreeEditForm';
import ParameterTreeView from './ParameterTreeView';

interface ParameterTreeNodeProps {
  param: Parameter;
  scopeType: 'global' | 'eventSpecific';
  allNodesMap: Map<string, Parameter>;
  toggleTreeNode: (path: string, type: 'global' | 'eventSpecific') => void;
  selectedEvent: EventSchema | null;
  handleParameterChange: (paramId: string, updates: Partial<Parameter>) => void;
  handleDeleteParameter: (paramId: string, deleteFromAllEvents?: boolean) => void;
  handleAddParameter: (eventId: string, parentParam: Parameter | null, targetScope: 'global' | 'eventSpecific') => void;
  isEditable: boolean;
  paramToEditId: string | null;
  clearParamToEditId: () => void;
  isLastChild?: boolean;
  activeEditingParamId: string | null;
  setActiveEditingParamId: React.Dispatch<React.SetStateAction<string | null>>;
  allEvents: EventSchema[];
  handleParameterAssociationChange: (propertyFullPath: string, eventId: string, isAssociated: boolean) => void;
  viewMode: ParameterViewMode;
  CustomContentComponent?: React.ComponentType<any>; // New prop for custom content rendering
  CustomContentComponentProps?: Record<string, any>; // Add prop to pass additional props to CustomContentComponent
}

const ParameterTreeNode: React.FC<ParameterTreeNodeProps> = ({
  param,
  scopeType,
  allNodesMap,
  toggleTreeNode,
  selectedEvent,
  handleParameterChange,
  handleDeleteParameter,
  handleAddParameter,
  isEditable,
  paramToEditId,
  clearParamToEditId,
  isLastChild,
  activeEditingParamId,
  setActiveEditingParamId,
  allEvents,
  handleParameterAssociationChange,
  viewMode,
  CustomContentComponent, // Destructure the new prop
  CustomContentComponentProps, // Destructure CustomContentComponentProps
}) => {
  // State for UI
  const [isEditing, setIsEditing] = useState(false);
  const [localIsExpanded, setLocalIsExpanded] = useState(param.isExpanded);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [dialogTitle, setDialogTitle] = useState('');
  const [dialogMessage, setDialogMessage] = useState('');
  const [dialogConfirmText, setDialogConfirmText] = useState('Delete');
  const [dialogCancelText, setDialogCancelText] = useState('Cancel');
  const [dialogOnConfirm, setDialogOnConfirm] = useState<(() => void) | null>(null);
  const [dialogOnThirdOption, setDialogOnThirdOption] = useState<(() => void) | null>(null);
  const [dialogThirdOptionText, setDialogThirdOptionText] = useState<string | undefined>(undefined);
  const [showDiscardConfirmation, setShowDiscardConfirmation] = useState(false);
  
  // Ref to track if we're the ones initiating the toggle
  const isTogglingRef = useRef(false);

  // Computed properties
  const hasChildren = param.children && param.children.length > 0;
  const indentationUnit = 20; // px per level
  const isExpandable = hasChildren || param.dataTypes.includes('array') || param.dataTypes.includes('object');
  
  // Sync local expanded state with prop, but only if we're not the ones toggling
  useEffect(() => {
    if (!isTogglingRef.current) {
      setLocalIsExpanded(param.isExpanded);
    } else {
      isTogglingRef.current = false;
    }
  }, [param.isExpanded]);

  // Effect to trigger edit mode for newly added parameters
  useEffect(() => {
    if (paramToEditId && param.id === paramToEditId) {
      setIsEditing(true);
      clearParamToEditId(); // Clear the ID in the parent to prevent re-triggering
      
      // Ensure the node is expanded if it's a parent
      if (hasChildren || param.dataTypes.includes('array') || param.dataTypes.includes('object')) {
        setLocalIsExpanded(true);
        isTogglingRef.current = true;
        toggleTreeNode(param.path, scopeType);
      }
    }
  }, [paramToEditId, param.id, clearParamToEditId, param.path, scopeType, toggleTreeNode, hasChildren, param.dataTypes]);
  
  // Effect to manage active editing parameter
  useEffect(() => {
    if (isEditing) {
      setActiveEditingParamId(param.id);
    } else if (activeEditingParamId === param.id) {
      setActiveEditingParamId(null);
    }
  }, [isEditing, param.id, setActiveEditingParamId, activeEditingParamId]);

  // Calculate "Used in X of Y events" for ParameterTreeView
  const usedInEventNames = allEvents
    .filter(event => event.parameterFullPaths.includes(param.fullPath))
    .map(event => event.name);
  const usedInEventCount = usedInEventNames.length;
  const totalEventCount = allEvents.length;

  // Handlers
  const onToggleTreeNode = useCallback(() => {
    setLocalIsExpanded(prevState => !prevState);
    isTogglingRef.current = true;
    toggleTreeNode(param.path, scopeType);
  }, [param.path, scopeType, toggleTreeNode]);

  const onDeleteParameter = useCallback(() => {
    if (!selectedEvent) return;

    const isGlobalProp = scopeType === 'global';
    const hasChildren = param.children && param.children.length > 0;

    if (isGlobalProp) {
      setDialogTitle('Delete Global Parameter');
      setDialogMessage(`This parameter is global. Do you want to delete it from "${selectedEvent.name}" only, or from all events?`);
      setDialogConfirmText('Delete Globally');
      setDialogOnConfirm(() => () => {
        handleDeleteParameter(param.id, true);
        setShowDeleteConfirmation(false);
      });
      setDialogThirdOptionText(`Delete from Event`);
      setDialogOnThirdOption(() => () => {
        handleDeleteParameter(param.id, false);
        setShowDeleteConfirmation(false);
      });
    } else {
      setDialogTitle('Delete Parameter');
      setDialogMessage(hasChildren ? 'This parameter has sub-properties. Are you sure you want to delete it and all its children?' : 'Are you sure you want to delete this parameter?');
      setDialogConfirmText('Delete');
      setDialogOnConfirm(() => () => {
        handleDeleteParameter(param.id);
        setShowDeleteConfirmation(false);
      });
      setDialogOnThirdOption(null);
      setDialogThirdOptionText(undefined);
    }
    setShowDeleteConfirmation(true);
  }, [selectedEvent, param.id, param.children, scopeType, handleDeleteParameter]);

  const cancelDelete = useCallback(() => {
    setShowDeleteConfirmation(false);
  }, []);

  const onAddParameter = useCallback(() => {
    if (selectedEvent) {
      handleAddParameter(selectedEvent.id, param, scopeType);
    }
  }, [selectedEvent, param, scopeType, handleAddParameter]);

  const startEditing = useCallback(() => {
    if (activeEditingParamId === null) {
      setIsEditing(true);
      setActiveEditingParamId(param.id);
    }
  }, [activeEditingParamId, param.id, setActiveEditingParamId]);

  const handleExitEditing = useCallback((hasUnsavedChanges: boolean, hasErrors: boolean) => {
    if (hasErrors) {
      // If there are errors, do not exit editing. The form component should handle re-focusing.
      return;
    }

    if (hasUnsavedChanges) {
      setDialogTitle('Discard Changes?');
      setDialogMessage('You have unsaved changes. Do you want to discard them?');
      setDialogConfirmText('Discard');
      setDialogOnConfirm(() => () => {
        // No need to revert local state here, as ParameterTreeEditForm will re-sync on re-render
        setIsEditing(false);
        setActiveEditingParamId(null); // Clear the active editing param ID
        setShowDiscardConfirmation(false);
      });
      setDialogCancelText('Keep Editing');
      setDialogOnThirdOption(null);
      setDialogThirdOptionText(undefined);
      setShowDiscardConfirmation(true);
    } else {
      setIsEditing(false);
      setActiveEditingParamId(null); // Clear the active editing param ID when exiting without unsaved changes
    }
  }, [setActiveEditingParamId]);
  
  return (
    <motion.div
      key={param.id}
      initial={false}
      animate={{ opacity: 1, height: 'auto' }}
      exit={{ opacity: 0, height: 0 }}
      transition={{ duration: 0.2 }}
    >
      <div 
        className={clsx(
          `relative group`,
          'cursor-pointer',
          'transition-colors',
        )}
      >
        {/* Vertical tree line for current level */}
        {(param.level || 0) > 0 && (
          <div
            className="absolute w-px bg-gray-300 dark:bg-gray-600"
            style={{ 
              left: `${(param.level || 0) * indentationUnit - (indentationUnit / 2)}px`, // Adjusted left
              top: 0, 
              height: isLastChild ? '24px' : '100%',
              opacity: 1
            }}
          />
        )}
        
        {/* Horizontal connector line */}
        {(param.level || 0) > 0 && (
            <div
              className="absolute h-px bg-gray-300 dark:bg-gray-600"
              style={{ 
                left: `${(param.level || 0) * indentationUnit - (indentationUnit / 2)}px`, // Adjusted left
                width: `${indentationUnit / 2}px`, // Connect to the button
                top: '24px', /* Aligned with the center of the expand button */
                opacity: 1
              }}
            />
        )}
        
        {/* Main content */}
        <div className="flex items-start gap-2" style={{ paddingLeft: `${(param.level || 0) * indentationUnit}px` }}> {/* Apply padding here */}
          {/* Expand/collapse button */}
          <div className="flex items-center h-10 mt-1">
            {isExpandable ? (
              <button
                onClick={onToggleTreeNode}
                className="relative z-10 flex w-6 h-6 justify-center items-center bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-md transition-colors"
              >
                {localIsExpanded ? (
                  <ChevronDown size={16} className="text-gray-600 dark:text-gray-300" />
                ) : (
                  <ChevronRight size={16} className="text-gray-600 dark:text-gray-300" />
                )}
              </button>
            ) : (
              <div className="w-2"></div>
            )}
          </div>
          
          {/* Parameter content */}
          <div className="flex-1 min-w-0 w-full pr-3">
            {isEditing && isEditable ? ( // Only show edit form if isEditable is true
              <ParameterTreeEditForm
                param={param}
                scopeType={scopeType}
                selectedEvent={selectedEvent}
                handleParameterChange={handleParameterChange}
                isEditable={isEditable}
                isEditing={isEditing}
                activeEditingParamId={activeEditingParamId}
                setActiveEditingParamId={setActiveEditingParamId}
                allEvents={allEvents}
                handleParameterAssociationChange={handleParameterAssociationChange}
                onExitEditing={handleExitEditing}
                viewMode={viewMode}
              />
            ) : (
              <div className="w-full"> {/* Ensure this div takes full width */}
                {CustomContentComponent ? ( // Render custom component if provided
                  <CustomContentComponent
                    param={param}
                    isEditable={isEditable} // Pass isEditable to custom component
                    totalEventCount={totalEventCount}
                    usedInEventCount={usedInEventCount}
                    startEditing={startEditing}
                    onAddParameter={onAddParameter}
                    onDeleteParameter={onDeleteParameter}
                    showDeleteConfirmation={showDeleteConfirmation}
                    hidePriorityPill={param.priorities.includes('Standard')}
                    hideRegex={true}
                    eventNames={usedInEventNames} // Pass eventNames to custom component
                    {...CustomContentComponentProps} // Spread additional props here
                  />
                ) : ( // Otherwise, render default ParameterTreeView
                  <ParameterTreeView
                    param={param}
                    isEditable={isEditable}
                    totalEventCount={totalEventCount}
                    usedInEventCount={usedInEventCount}
                    eventNames={usedInEventNames} // Pass eventNames to default ParameterTreeView
                    startEditing={startEditing}
                    onAddParameter={onAddParameter}
                    onDeleteParameter={onDeleteParameter}
                    showDeleteConfirmation={showDeleteConfirmation}
                    hidePriorityPill={param.priorities.includes('Standard')}
                    hideRegex={true}
                  />
                )}
              </div>
            )}
          </div>
        </div>
        
        {/* Delete Confirmation Dialog */}
        {showDeleteConfirmation && dialogOnConfirm && (
          <ConfirmDialog
            onClose={cancelDelete}
            onConfirm={dialogOnConfirm}
            title={dialogTitle}
            message={dialogMessage}
            confirmText={dialogConfirmText}
            cancelText={dialogCancelText}
            isDanger={true}
            onThirdOption={dialogOnThirdOption || undefined}
            thirdOptionText={dialogThirdOptionText}
            thirdOptionColor="secondary"
          />
        )}
        
        {/* Discard Changes Confirmation Dialog */}
        {showDiscardConfirmation && (
          <ConfirmDialog
            onClose={() => setShowDiscardConfirmation(false)}
            onConfirm={() => {
              setIsEditing(false); // Discard changes by simply exiting editing mode
              setShowDiscardConfirmation(false);
            }}
            title={dialogTitle}
            message={dialogMessage}
            confirmText={dialogConfirmText}
            cancelText={dialogCancelText}
            isDanger={false}
          />
        )}

        {/* Children */}
        {hasChildren && localIsExpanded && (
          <motion.div
            initial={false}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
          >
            {param.children && param.children.map((child, index) => (
              <ParameterTreeNode
                key={child.id}
                param={child}
                scopeType={scopeType}
                allNodesMap={allNodesMap}
                toggleTreeNode={toggleTreeNode}
                selectedEvent={selectedEvent}
                handleParameterChange={handleParameterChange}
                handleDeleteParameter={handleDeleteParameter}
                handleAddParameter={handleAddParameter}
                isEditable={isEditable}
                paramToEditId={paramToEditId}
                clearParamToEditId={clearParamToEditId}
                isLastChild={index === param.children!.length - 1}
                activeEditingParamId={activeEditingParamId}
                setActiveEditingParamId={setActiveEditingParamId}
                allEvents={allEvents}
                handleParameterAssociationChange={handleParameterAssociationChange}
                viewMode={viewMode}
                CustomContentComponent={CustomContentComponent} // Pass CustomContentComponent to children
                CustomContentComponentProps={CustomContentComponentProps} // Pass CustomContentComponentProps to children
              />
            ))}
          </motion.div>
        )}
      </div>
    </motion.div>
  );
};

export default ParameterTreeNode;
